<script>
    import Preview from "./Preview.svelte";
    import { hostname, isAuthenticated, stack, showDebug } from "../stores.js";
    import { selectedPreview, finalFdkSettings, lastPreviewImageIndex, lastCentershiftPreviewImageIndex } from "../previewstores.js";
    import { extractPhaseRetrievalBaseFromPath, formatJson, checkForErrorsInJson, isNumeric, now, runPreviewApi, phaseRetrievalComparator } from "../utils.js";
    import Modal from "./Modal.svelte";
    import { toast } from "@zerodevx/svelte-toast";
    import { toastThemeError, toastThemeInfo } from "../toasts.js";
    import { pipelineObserver } from '../pipelinestores.js';
    import { tooltip } from "svooltip";
    import 'svooltip/styles.css';

    /**
     * Phase retrieval preview is meant to be visited after centershift preview.
     * There is no specific phase retrieval preview image generated by the datapipeline.
     * In fact, it is part of the preprocessing chain, and the output is still
     * projection images.
     *
     * The user will be able to switch between one or more generated phase retrieval datasets.
     * The selected phase retrieval dataset works like a filter such that only (centershift
     * and beamhard cupping) previews based on that dataset are visible.
     *
     * The phase retrieval presentation is a centershift image. It is assumed that the user has
     * already chosen a good centershift value, so we can steal the latest middle centershift
     * preview image.
     *
     * The parameters that are relevant for manipulation are delta and mu. These come from
     * the centershift preview object (and should of course be the same as the imput to the
     * phase retrieval dataset that this preview was based on).
     *
     * The user may change the delta and mu values and if these settings have not been
     * generated before (i.e. there is no phase retrieval dataset available with these values),
     * he can choose to start a new preview calculation.
     *
     * This will spawn a new pipeline where a new phase retrieval dataset will be calculated,
     * followed by a centershift preview reconstruction using the current centershift settings,
     * as well as a beamhard cupping preview set.
     *
     */

    export let params;
    export let centershiftSlices;
    export let sliceIndex;
    let CENTERSHIFTSLIDERVALUE_DEFAULT = 31;
    let deltaval;
    let muval;
    export let phaseRetrievalDatasetIds;
    export let phaseRetrievalSlicePreviews; // Really, these are centershift previews, but the array should not be updated when the centershiftSlicePreviews in Recipe changes
    let centershiftPreviewImageIndex = CENTERSHIFTSLIDERVALUE_DEFAULT;
    let showModal = false;

    $: initPreview($selectedPreview);
    $: onChangedDeltaValue(deltaval);
    $: onChangedMuValue(muval);

    function findMatchingPhaseRetrievalDatasets(delta, mu) {
        // Keep only the centershift previews where delta and mu are as selected
        const matchingCentershiftPreviews = phaseRetrievalSlicePreviews?.filter((p) => {
            if (
                p.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.delta?.toPrecision(
                    2
                ) == (delta / 1.0)?.toPrecision(2) &&
                p.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.mu?.toPrecision(3) ==
                    (mu / 1.0)?.toPrecision(3)
            ) {
                return true;
            } else {
                return false;
            }
        });

        // Extract the phase retrieval bases for all found previews that match settings
        let phaseRetrievalBases = matchingCentershiftPreviews?.map((p) => {
            const base = extractPhaseRetrievalBaseFromPath(p?.preview_output?.[0]?.large_image_pattern);
            console.log("PH BASE=", base);
            return base;
        });
        phaseRetrievalBases = [...new Set(phaseRetrievalBases)]; // Remove duplicates
        console.log("PHASE RETRIEVAL BASES MATCHING DELTA=", delta, " AND MU=", mu, ":", phaseRetrievalBases);
        return phaseRetrievalBases;
    }

    function updateFdkSettings(options) {
        $finalFdkSettings = {
            ...$finalFdkSettings,
            ...options,
        };

        console.log("FINAL FDK SETTINGS ARE NOW: ", $finalFdkSettings);
    }

    let showAddPreviewButton = false;
    function onChangedDeltaValue(deltaval) {
        let changedSettings = { delta: deltaval / 1.0 };
        let phaseRetrievalBases = findMatchingPhaseRetrievalDatasets(deltaval, muval);
        console.log("PH DATASETS=", phaseRetrievalBases);
        if (phaseRetrievalBases?.[0]) {
            showAddPreviewButton = false;
            phaseRetrievalDatasetIds = phaseRetrievalBases;
            changedSettings.phase_retrieval_dataset_id = phaseRetrievalBases[0];
        } else {
            showAddPreviewButton = true;
        }
        console.log("PHASE_RETRIEVAL CHANGED FDK SETTINGS TO: ", changedSettings);
        updateFdkSettings(changedSettings);
    }

    function onChangedMuValue(muval) {
        let changedSettings = { mu: muval / 1.0 };
        let phaseRetrievalBases = findMatchingPhaseRetrievalDatasets(deltaval, muval);
        console.log("PH DATASETS=", phaseRetrievalBases);
        if (phaseRetrievalBases?.[0]) {
            showAddPreviewButton = false;
            phaseRetrievalDatasetIds = phaseRetrievalBases;
            changedSettings.phase_retrieval_dataset_id = phaseRetrievalBases[0];
        } else {
            showAddPreviewButton = true;
        }
        console.log("PHASE_RETRIEVAL CHANGED FDK SETTINGS TO: ", changedSettings);
        updateFdkSettings(changedSettings);
    }

    function startAddPhaseRetrieval(deltaval, muval) {
        // Typically, we would want to generate wide centershift previews for each
        // slice that already exists for this tomography.
        phaseRetrievalPreviewSettings = {
            generic_preview_input: {
                preprocessing_settings: {
                    // Empty but needed for API syntax
                },
                phase_retrieval_settings: {
                    phase_retrieval: {
                        delta: deltaval,
                        mu: muval
                    }
                },
                reconstruction_settings: {
                    // For wide centershift, we use the calibrated centershift, so leave blank here
                    //centershift_object_mm: $finalFdkSettings?.centershift_object_mm
                }
            }
        };

        // Delta
        if (isNumeric(deltaval)) {
            let delta = parseFloat(deltaval);
            phaseRetrievalPreviewSettings.generic_preview_input.phase_retrieval_settings.phase_retrieval.delta = delta;
        } else {
            //delete phaseRetrievalPreviewSettings.generic_preview_input.phase_retrieval_settings.phase_retrieval.delta;
            // Setting delta to null here so that it can be caught by the json error checker
            phaseRetrievalPreviewSettings.generic_preview_input.phase_retrieval_settings.phase_retrieval.delta = null;
        }

        // Mu
        if (isNumeric(muval)) {
            let mu = parseFloat(muval);
            phaseRetrievalPreviewSettings.generic_preview_input.phase_retrieval_settings.phase_retrieval.mu = mu;
        } else {
            //delete phaseRetrievalPreviewSettings.generic_preview_input.phase_retrieval_settings.phase_retrieval.mu;
            // Setting mu to null here so that it can be caught by the json error checker
            phaseRetrievalPreviewSettings.generic_preview_input.phase_retrieval_settings.phase_retrieval.mu = null;
        }

        phaseRetrievalPreviewSettings.previews = [];
        phaseRetrievalPreviewSettings.previews.push(previewWideCentershift);

        // Slices for which to generate previews
        let newPreviewSlicesToGenerate = [...new Set(centershiftSlices)]; // Deduplicate centershift slices
        phaseRetrievalPreviewSettings.generic_preview_input.slices = newPreviewSlicesToGenerate;

        //////////////////////////// BEGIN EXPERIMENTAL ////////////////////////////
        // Experimental: Generate beamhard_cupping previews for the currently selected centershift value.
        // Please note that this is NOT the same as the calibrated centershift.
        // The pipeline can only take one centershift value, so we have to create a second pipeline.
        let phaseRetrievalPreviewSettingsForBeamhardCupping = {
            generic_preview_input: {
                preprocessing_settings: {
                    // Empty but needed for API syntax
                },
                phase_retrieval_settings: {
                    phase_retrieval: {
                        delta: deltaval,
                        mu: muval
                    }
                },
                reconstruction_settings: {
                    centershift_object_mm: $finalFdkSettings?.centershift_object_mm
                }
            }
        };

        // Delta
        if (isNumeric(deltaval)) {
            let delta = parseFloat(deltaval);
            phaseRetrievalPreviewSettingsForBeamhardCupping.generic_preview_input.phase_retrieval_settings.phase_retrieval.delta = delta;
        } else {
            delete phaseRetrievalPreviewSettingsForBeamhardCupping.generic_preview_input.phase_retrieval_settings.phase_retrieval.delta;
        }

        // Mu
        if (isNumeric(deltaval)) {
            let mu = parseFloat(muval);
            phaseRetrievalPreviewSettingsForBeamhardCupping.generic_preview_input.phase_retrieval_settings.phase_retrieval.mu = mu;
        } else {
            delete phaseRetrievalPreviewSettingsForBeamhardCupping.generic_preview_input.phase_retrieval_settings.phase_retrieval.mu;
        }

        phaseRetrievalPreviewSettingsForBeamhardCupping.previews = [];
        phaseRetrievalPreviewSettingsForBeamhardCupping.previews.push(previewBeamhardCupping); // Ask for beamhard cupping preview because the user will need it later

        // Slices for which to generate previews
        let newBeamhardCuppingPreviewSlicesToGenerate = [...new Set(centershiftSlices)]; // Deduplicate centershift slices
        phaseRetrievalPreviewSettingsForBeamhardCupping.generic_preview_input.slices = newBeamhardCuppingPreviewSlicesToGenerate;
        //////////////////////////// END EXPERIMENTAL ////////////////////////////

        showModal = true;
    }

    let phaseRetrievalPreviewSettings;
    function startPhaseRetrievalPreview(preview, deltaval, muval) {
        phaseRetrievalPreviewSettings = { ...$selectedPreview.preview_input };
        const existingPreviewInput = phaseRetrievalPreviewSettings?.preview_input;
        delete phaseRetrievalPreviewSettings?.preview_input;

        // Don't include optimisation flags that may have been set by pipeline
        delete phaseRetrievalPreviewSettings.generic_preview_input.reconstruction_settings.centershift_is_optimized;
        delete phaseRetrievalPreviewSettings.generic_preview_input.reconstruction_settings.tilt_is_optimized;

        phaseRetrievalPreviewSettings.previews = [existingPreviewInput];
        phaseRetrievalPreviewSettings.previews?.push(previewBeamhardCupping); // Ask for beamhard cupping preview because the user will need it later

        // Slices for which to generate previews
        let newPreviewSlicesToGenerate = [...new Set(centershiftSlices)]; // Deduplicate centershift slices
        phaseRetrievalPreviewSettings.generic_preview_input.slices = newPreviewSlicesToGenerate;

        // Delta
        if (isNumeric(deltaval)) {
            let delta = parseFloat(deltaval);
            phaseRetrievalPreviewSettings.generic_preview_input.phase_retrieval_settings.phase_retrieval.delta = delta;
        } else {
            delete phaseRetrievalPreviewSettings.generic_preview_input.phase_retrieval_settings.phase_retrieval.delta;
        }

        // Mu
        if (isNumeric(deltaval)) {
            let mu = parseFloat(muval);
            phaseRetrievalPreviewSettings.generic_preview_input.phase_retrieval_settings.phase_retrieval.mu = mu;
        } else {
            delete phaseRetrievalPreviewSettings.generic_preview_input.phase_retrieval_settings.phase_retrieval.mu;
        }

        showModal = true;
    }

    const previewCentershift = {
        preview_type: "CENTERSHIFT",
        centershift_tolerance_object_mm: 0.2, // TODO: Set initial value to something that depends on camera
    };

    const previewCentershiftOptimised = {
        preview_type: "CENTERSHIFT",
    };

    const previewBeamhardCupping = {
        preview_type: "BEAMHARD_CUPPING",
    };

    const previewWideCentershift = {
        preview_type: "WIDE_CENTERSHIFT",
    };

    async function onConfirmPhaseRetrievalPreviewSettings() {
        console.log("OK, CALLING PREVIEW PIPELINE FOR CENTERSHIFT...", params?.session);
        const previewInput = phaseRetrievalPreviewSettings;
        const runPreviewApiResponse = await runPreviewApi(
            $hostname,
            $isAuthenticated,
            $stack,
            params.session,
            params.tomography,
            previewInput
        );

        if (isNumeric(runPreviewApiResponse)) {
            let msg = `${now()}<br/>Run preview API error: ${runPreviewApiResponse}`;
            toast.push(msg, toastThemeError);
            return;
        }

        // The API should respond with a fully qualified pipeline name
        let pipelineId = runPreviewApiResponse?.id;

        // Indicate to the user that a new preview job has been started
        let msg = `${now()}<br/>`;
        msg += `Started phase retrieval preview at slice ${phaseRetrievalPreviewSettings?.generic_preview_input?.slices}
            with &delta;=${phaseRetrievalPreviewSettings.generic_preview_input.phase_retrieval_settings.phase_retrieval.delta}
            and &mu;=${phaseRetrievalPreviewSettings.generic_preview_input.phase_retrieval_settings.phase_retrieval.mu}<br/>
            for ${params.session}/${params.tomography}`;
        if (pipelineId) {
            msg += ` <br/>(${pipelineId})`;
        }
        toast.push(msg, toastThemeInfo);

        // Add the new pipeline to the pipeline observables list
        $pipelineObserver.addPipelineToObserve(pipelineId);
    }

    let showJson = false;
    function onShowJson() {
        showJson = true;
    }

    function resetPhaseRetrievalFields() {
        // NOTE: This value will be shown in the delta/mu input fields, so it might be nice to show a rounded value.
        // But it will also be used as current settings, and thereby when generating new slices, so to not confuse the
        // datapipeline, we need to keep the exact value.
        deltaval =
            $selectedPreview?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.delta;
        muval =
            $selectedPreview?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.mu;
    }

    function initPreview($selectedPreview) {
        if ($selectedPreview) {
            console.log("++++++++ INIT PHASE RETRIEVAL PREVIEW -- ", $selectedPreview);
            //const currentPreviewOutputIndex = $selectedPreview?.preview_output?.findIndex(s => s.slice == sliceIndex) || 0;
            /*if (isNumeric($lastPreviewImageIndex)) {
                centershiftPreviewImageIndex = $lastPreviewImageIndex;
            } else {
                // We have no existing image selected, so default to middle value
                const centershiftIndexRange = $selectedPreview?.preview_output?.[currentPreviewOutputIndex]?.parameter_values?.length;
                centershiftPreviewImageIndex = Math.floor((centershiftIndexRange - 1) / 2);
            }*/
            // Previously: This value (undefined) will be passed on to the Preview component, where it may set to something known
            // New behaviours: Use last known centershift preview image index
            centershiftPreviewImageIndex = $lastCentershiftPreviewImageIndex;
            resetPhaseRetrievalFields();
            onChangedDeltaValue(deltaval);
            onChangedMuValue(muval);
        }
    }

    function getHighestPhaseRetrieval(availableSlicePreviews) {
        let tmpAvailableSlicePreviews = [...availableSlicePreviews];
        let tmpAvailablePhs = tmpAvailableSlicePreviews.map(p => p.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval);
        tmpAvailablePhs?.sort(phaseRetrievalComparator);
        const arrayLength = tmpAvailablePhs?.length;
        if (!arrayLength) return undefined;
        const delta = tmpAvailablePhs?.[arrayLength-1].delta;
        const mu = tmpAvailablePhs?.[arrayLength-1].mu;
        const deltaByMu = delta / mu;
        return {
            delta: delta,
            mu: mu, 
            deltaByMu: deltaByMu
        };
    }

    function getLowestPhaseRetrieval(availableSlicePreviews) {
        let tmpAvailableSlicePreviews = [...availableSlicePreviews];
        let tmpAvailablePhs = tmpAvailableSlicePreviews.map(p => p.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval);
        tmpAvailablePhs?.sort(phaseRetrievalComparator);
        const arrayLength = tmpAvailablePhs?.length;
        if (!arrayLength) return undefined;
        const delta = tmpAvailablePhs?.[0].delta;
        const mu = tmpAvailablePhs?.[0].mu;
        const deltaByMu = delta / mu;
        return {
            delta: delta,
            mu: mu, 
            deltaByMu: deltaByMu
        };
    }

    function formatPhaseRetrievalValues(ph) {
        return `δ:${ph?.delta} / μ:${ph?.mu} = ${ph?.deltaByMu}`;
    }
</script>

<Preview
    headerText="Phase retrieval preview"
    type="PHASE_RETRIEVAL"
    {centershiftSlices}
    {sliceIndex}
    availableSlicePreviews={phaseRetrievalSlicePreviews}
    previewImageIndex={centershiftPreviewImageIndex}
    on:carousel>
    <phaseretrieval-advanced>
        <form class="phaseretrieval-form">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label><span use:tooltip={{content: "Decrement component of the complex refractive index", placement: "top"}}>&delta;</span></label>
            <input type="number" step="any" bind:value={deltaval} placeholder="" />
            <!--annotation>&nbsp;</annotation-->
            <!-- experimental helper to choose more phase
            <button class={phaseRetrievalDatasetIds?.length > 0 ? "" : "disabled"}
                on:click|preventDefault|stopPropagation={() => {
                    deltaval = getHighestPhaseRetrieval(phaseRetrievalSlicePreviews)?.delta * 3;
                }}>
                3 x more
            </button>
            -->
            <span></span>
            <button
                class={showAddPreviewButton ? "" : "disabled"}
                on:click|preventDefault|stopPropagation={() => {
                    //startPhaseRetrievalPreview($selectedPreview?.preview_output?.[0].slice, deltaval, muval);
                    startAddPhaseRetrieval(deltaval, muval);
                }}>
                Add preview...
            </button>
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label><span use:tooltip={{content: "Linear attenuation coefficient", placement: "top"}}>&mu;</span></label>
            <input type="number" step="any" bind:value={muval} placeholder="e.g. 500" />
            <!--annotation>&nbsp;</annotation-->
            <!-- experimental helper to choose less phase
            <button class={phaseRetrievalDatasetIds?.length > 0 ? "" : "disabled"}
                on:click|preventDefault|stopPropagation={() => {
                    deltaval = Math.round((getLowestPhaseRetrieval(phaseRetrievalSlicePreviews)?.delta / 10 * 3) * 1e11) / 1e11;
                }}>
                3 x less
            </button>
            -->
            <span></span>
            
        </form>
        {#if $showDebug}
            <annotation style="margin-left: 0; font-family: monospace; display: block; line-break: anywhere; max-width: 768px;">ph ids: {phaseRetrievalDatasetIds}&nbsp;
                lowest: {formatPhaseRetrievalValues(getLowestPhaseRetrieval(phaseRetrievalSlicePreviews))} &nbsp;
                highest: {formatPhaseRetrievalValues(getHighestPhaseRetrieval(phaseRetrievalSlicePreviews))}
            </annotation>
        {/if}
    </phaseretrieval-advanced>
    {#if showModal}
        <Modal
            on:close={() => {
                showJson = false;
                showModal = false;
            }}
            on:ok={() => {
                showModal = false;
                onConfirmPhaseRetrievalPreviewSettings();
                showJson = false;
            }}
            on:json={onShowJson}
            jsonable={true}
            dataError={checkForErrorsInJson(phaseRetrievalPreviewSettings)}>
            <h2 slot="header">Confirm phase retrieval preview settings</h2>
            {#if showJson}
                <pre>{formatJson(phaseRetrievalPreviewSettings)}</pre>
            {:else}
                <h3>Preprocessing settings</h3>
                <settings>
                    delta: {phaseRetrievalPreviewSettings?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.delta?.toPrecision() || null}
                    <br />
                    mu: {phaseRetrievalPreviewSettings?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.mu?.toPrecision(
                        3
                    ) || null}
                    <br />
                </settings>
                <h3>Reconstruction settings</h3>
                <settings>
                    {#if phaseRetrievalPreviewSettings?.generic_preview_input?.reconstruction_settings
                        ?.centershift_tolerance_object_mm}
                        centershift: {phaseRetrievalPreviewSettings?.generic_preview_input?.reconstruction_settings?.centershift_object_mm} mm
                    {/if}
                    <br />
                    {#if phaseRetrievalPreviewSettings?.generic_preview_input?.reconstruction_settings?.tilt_deg}
                        tilt: {phaseRetrievalPreviewSettings?.generic_preview_input?.reconstruction_settings?.tilt_deg}°
                        <br />
                    {/if}
                    slices: {phaseRetrievalPreviewSettings?.generic_preview_input?.slices}
                </settings>
            {/if}
        </Modal>
    {/if}
    <!--DEBUG: phaseRetrievalDatasetIds: {phaseRetrievalDatasetIds} -->
</Preview>

<style>
    phaseretrieval-advanced {
        display: block;
    }
    .phaseretrieval-form {
        display: grid;
        grid-template-columns: 3% 20% 35% 29%;
        grid-column-gap: 2.5%;
        align-items: baseline;
    }
    .phaseretrieval-form button {
        margin-left: 2em;
    }

    settings {
        font-family: monospace;
    }
</style>
